import moment from 'moment';
import { useMemo } from 'react';

import { checkSubscriptionGracePeriod } from '../../../api/Subscription';
import ErrorIcon from '../../../assets/svg/error-icon-white.svg';
import InfoIcon from '../../../assets/svg/info-white.svg';
import warningIcon from '../../../assets/svg/warning-icon.svg';
import { useManageSubscription } from '../../../hooks';
import BasicSpinner from '../../BasicSpinner';
import InfoModal from '../../Modal/InfoModal';
import AlertBar from '../AlertBar';

interface SubscriptionExpiredAlertProps {
  renewalDate: number;
  isTrial?: boolean;
}

const SubscriptionExpiredAlert: React.FC<SubscriptionExpiredAlertProps> = ({ renewalDate, isTrial }) => {
  const { redirecting, errorMessage, setErrorMessage, onManageSubscription } = useManageSubscription();
  const isBeyondGrace = useMemo(() => checkSubscriptionGracePeriod(renewalDate), [renewalDate]);

  const trialDaysLeft = useMemo(() => {
    const now = moment().startOf('day');
    const trialEnd = moment.unix(renewalDate).startOf('day');
    return trialEnd.diff(now, 'days');
  }, [renewalDate]);

  const isTrialEndingSoon = useMemo(
    () => isTrial === true && trialDaysLeft >= 0 && trialDaysLeft <= 7,
    [isTrial, trialDaysLeft]
  );

  const modalContent = useMemo(
    () => (
      <div>
        <div className="text-center mb-3">
          <img src={warningIcon} alt="Warning icon" />
        </div>
        <div className="text-center mb-3">Something went wrong: {errorMessage}</div>
      </div>
    ),
    [errorMessage]
  );

  // Banner logic:
  // - Beyond grace: always show expired banner.
  // - Trial (price 0): show trial ending banner in the last 7 days before renewalDate.
  // - Not trial: show renewal-failed banner.
  // - Unknown trial status: default to no banner.
  if (!isBeyondGrace) {
    if (isTrial === true && !isTrialEndingSoon) return null;
    if (isTrial !== true && isTrial !== false) return null;
  }

  let message;
  let icon = ErrorIcon;
  let variant: string = 'danger';

  if (isBeyondGrace) {
    message = (
      <>
        Your subscription has expired. We’ve stopped optimising your battery. <u>Resubscribe here</u>.
      </>
    );
  } else if (isTrialEndingSoon) {
    if (trialDaysLeft === 0) {
      message = (
        <>
          Free Trial ends today on {moment.unix(renewalDate).format('Do MMMM YYYY')} and you will automatically move to
          a paid subscription.
        </>
      );
    } else {
      const dayWord = trialDaysLeft === 1 ? 'day' : 'days';
      message = (
        <>
          Free Trial ends in {trialDaysLeft} {dayWord} on {moment.unix(renewalDate).format('Do MMMM YYYY')} and you will
          automatically move to a paid subscription.
        </>
      );
    }
    icon = InfoIcon;
    variant = 'dark';
  } else {
    message = (
      <>
        We couldn’t renew your subscription. <u>Update your card details to stay active</u>.
      </>
    );
  }

  return (
    <>
      <InfoModal show={!!errorMessage} onClose={() => setErrorMessage(undefined)} infoContent={modalContent} />
      <AlertBar
        label={
          <div>
            {message}
            {redirecting && <BasicSpinner color="white" />}
          </div>
        }
        icon={icon}
        iconSize={20}
        onClick={onManageSubscription}
        variant={variant}
      />
    </>
  );
};

export default SubscriptionExpiredAlert;
